import React, { useState, useCallback } from "react";
import { XMarkIcon, ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import axios from "axios";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";

/**
 * ImportCSVModal - A modal component for importing CSV data
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Close handler
 * @param {Array} columns - Available columns for mapping
 * @param {string} tableName - Target table name for import
 * @param {Array} relationshipMappings - Config for resolving relationships by name
 * @param {function} onImportComplete - Callback after successful import
 */
const ImportCSVModal = ({
    isOpen,
    onClose,
    columns,
    tableName,
    relationshipMappings = [],
    onImportComplete,
}) => {
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [columnMappings, setColumnMappings] = useState({});
    const [isImporting, setIsImporting] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [error, setError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    // Parse CSV content
    const parseCSV = (content) => {
        const lines = content.split("\n").filter((line) => line.trim());
        if (lines.length === 0) return { headers: [], data: [] };

        // Parse headers
        const headerLine = lines[0];
        const csvHeaders = headerLine.split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const values = [];
            let inQuotes = false;
            let currentValue = "";

            for (let j = 0; j < line.length; j++) {
                const char = line[j];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === "," && !inQuotes) {
                    values.push(currentValue.trim());
                    currentValue = "";
                } else {
                    currentValue += char;
                }
            }
            values.push(currentValue.trim());

            if (values.length === csvHeaders.length) {
                const row = {};
                csvHeaders.forEach((header, idx) => {
                    row[header] = values[idx];
                });
                data.push(row);
            }
        }

        return { headers: csvHeaders, data };
    };

    // Handle file selection
    const handleFileChange = useCallback(async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setError(null);
        setImportResult(null);
        setIsParsing(true);

        try {
            const text = await selectedFile.text();
            const { headers: csvHeaders, data } = parseCSV(text);

            setHeaders(csvHeaders);
            setParsedData(data);

            // Auto-map columns by matching names
            const autoMappings = {};
            csvHeaders.forEach((csvHeader) => {
                const lowerHeader = csvHeader.toLowerCase().replace(/[_\s]/g, "");
                const matchedColumn = columns.find((col) => {
                    const lowerColField = col.field.toLowerCase().replace(/[_\s]/g, "");
                    const lowerColLabel = col.label.toLowerCase().replace(/[_\s]/g, "");
                    return lowerColField === lowerHeader || lowerColLabel === lowerHeader;
                });
                if (matchedColumn) {
                    autoMappings[csvHeader] = matchedColumn.field;
                }
            });
            setColumnMappings(autoMappings);
        } catch (err) {
            setError("Failed to parse CSV file. Please check the file format.");
            console.error("CSV parse error:", err);
        } finally {
            setIsParsing(false);
        }
    }, [columns]);

    // Handle drag events
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile.type === "text/csv" || droppedFile.name.endsWith(".csv")) {
                handleFileChange({ target: { files: [droppedFile] } });
            } else {
                setError("Please upload a CSV file.");
            }
        }
    }, [handleFileChange]);

    // Update column mapping
    const updateMapping = (csvHeader, tableField) => {
        setColumnMappings((prev) => ({
            ...prev,
            [csvHeader]: tableField,
        }));
    };

    // Handle import
    const handleImport = async () => {
        if (parsedData.length === 0) {
            setError("No data to import.");
            return;
        }

        const mappedFields = Object.values(columnMappings).filter(Boolean);
        if (mappedFields.length === 0) {
            setError("Please map at least one column.");
            return;
        }

        setIsImporting(true);
        setError(null);

        try {
            // Transform data according to mappings
            const transformedData = parsedData.map((row) => {
                const newRow = {};
                Object.entries(columnMappings).forEach(([csvHeader, tableField]) => {
                    if (tableField && row[csvHeader] !== undefined) {
                        // Convert numeric strings to numbers
                        const value = row[csvHeader];
                        if (!isNaN(parseFloat(value)) && isFinite(value)) {
                            newRow[tableField] = parseFloat(value);
                        } else {
                            newRow[tableField] = value;
                        }
                    }
                });
                return newRow;
            });

            const response = await axios.post(
                `${config.apiUrl}/api/imports/import-csv`,
                {
                    tableName,
                    data: transformedData,
                    relationshipMappings,
                },
                {
                    headers: {
                        Authorization: `Bearer ${getCookie("_AT")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setImportResult({
                success: true,
                message: response.data.message || `Successfully imported ${response.data.successCount} records.`,
                successCount: response.data.successCount,
                errorCount: response.data.errorCount,
                errors: response.data.errors,
            });

            if (onImportComplete) {
                onImportComplete();
            }
        } catch (err) {
            setError(err.response?.data?.message || "Import failed. Please try again.");
            console.error("Import error:", err);
        } finally {
            setIsImporting(false);
        }
    };

    // Reset and close
    const handleClose = () => {
        if (isImporting) return;
        setFile(null);
        setParsedData([]);
        setHeaders([]);
        setColumnMappings({});
        setError(null);
        setImportResult(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Import CSV</h3>
                    <button
                        onClick={handleClose}
                        className={`text-gray-400 hover:text-gray-600 ${isImporting ? "cursor-not-allowed opacity-50" : ""
                            }`}
                        disabled={isImporting}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {/* Success Result */}
                    {importResult?.success && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex items-center gap-2 text-green-700">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">{importResult.message}</span>
                            </div>
                            {importResult.errorCount > 0 && (
                                <p className="mt-2 text-sm text-yellow-700">
                                    {importResult.errorCount} records failed to import.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {/* File Upload Area */}
                    {!file && !importResult?.success && (
                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
                                }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <ArrowUpTrayIcon className="h-12 w-12 mx-auto text-gray-400" />
                            <p className="mt-2 text-sm text-gray-600">
                                Drag and drop your CSV file here, or{" "}
                                <label className="text-blue-600 hover:text-blue-700 cursor-pointer underline">
                                    browse
                                    <input
                                        type="file"
                                        accept=".csv"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                </label>
                            </p>
                            <p className="mt-1 text-xs text-gray-500">CSV files only</p>
                        </div>
                    )}

                    {/* Parsing indicator */}
                    {isParsing && (
                        <div className="flex items-center justify-center gap-3 py-4">
                            <svg
                                className="animate-spin h-5 w-5 text-blue-600"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="text-sm text-gray-600">Parsing file...</span>
                        </div>
                    )}

                    {/* File Info & Column Mapping */}
                    {file && parsedData.length > 0 && !importResult?.success && (
                        <>
                            <div className="bg-gray-50 rounded-md p-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">{file.name}</p>
                                        <p className="text-xs text-gray-500">{parsedData.length} rows found</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setFile(null);
                                            setParsedData([]);
                                            setHeaders([]);
                                            setColumnMappings({});
                                        }}
                                        className="text-sm text-red-600 hover:text-red-700"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>

                            {/* Column Mapping */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Column Mapping</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {headers.map((header) => (
                                        <div key={header} className="flex items-center gap-3">
                                            <span className="text-sm text-gray-600 w-1/3 truncate" title={header}>
                                                {header}
                                            </span>
                                            <span className="text-gray-400">→</span>
                                            <select
                                                value={columnMappings[header] || ""}
                                                onChange={(e) => updateMapping(header, e.target.value)}
                                                className="flex-1 text-sm border border-gray-300 rounded-md py-1.5 px-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">-- Skip this column --</option>
                                                {columns.map((col) => (
                                                    <option key={col.field} value={col.field}>
                                                        {col.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Preview */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Preview (first 3 rows)
                                </h4>
                                <div className="overflow-x-auto border rounded-md">
                                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {headers.map((header) => (
                                                    <th
                                                        key={header}
                                                        className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {parsedData.slice(0, 3).map((row, idx) => (
                                                <tr key={idx}>
                                                    {headers.map((header) => (
                                                        <td key={header} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                                            {row[header] || "-"}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Importing indicator */}
                    {isImporting && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-center gap-3">
                                <svg
                                    className="animate-spin h-5 w-5 text-blue-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span className="text-sm text-blue-700 font-medium">
                                    Importing {parsedData.length} records...
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                    <button
                        type="button"
                        onClick={handleClose}
                        className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 ${isImporting ? "cursor-not-allowed opacity-50" : ""
                            }`}
                        disabled={isImporting}
                    >
                        {importResult?.success ? "Close" : "Cancel"}
                    </button>
                    {!importResult?.success && (
                        <button
                            type="button"
                            onClick={handleImport}
                            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 inline-flex items-center gap-2 ${isImporting || parsedData.length === 0 ? "cursor-not-allowed opacity-50" : ""
                                }`}
                            disabled={isImporting || parsedData.length === 0}
                        >
                            {isImporting && (
                                <svg
                                    className="animate-spin h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                            {isImporting ? "Importing..." : "Import"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportCSVModal;
